import { FC, useMemo } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, Card, CardContent, Checkbox, Chip, Divider, FormControlLabel, FormHelperText, MenuItem, Stack, TextField } from '@mui/material'

import { Analyst } from '../../../../../types/Analyst'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { WysiwygEditorConfig } from '../../../../components/InputTranslator/InputTranslatorDialog/InputTranslatorDialog'
import { InputTranslatorType } from '../../../../types/InputTranslatorType'
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../../../editorial-cards/helpers/editorialCardHelpers'
import { hasAllowedUsers, ReportFormValues, reportVisibilityOptions, toTranslations } from '../../helpers/reportHelpers'
import { InternalReportTag } from '../../types/InternalReport'
import { ReportAllowedUsersField } from '../ReportAllowedUsersField/ReportAllowedUsersField'
import { ReportAnalystsField } from '../ReportAnalystsField/ReportAnalystsField'
import { ReportCoversField } from '../ReportCoversField/ReportCoversField'
import { ReportFileField } from '../ReportFileField/ReportFileField'
import { ReportGamesField } from '../ReportGamesField/ReportGamesField'

// Without the default `liveEventLink` plugin, which only works in Live Events Tracking.
const summaryEditorConfig: WysiwygEditorConfig = {
  plugins: ['link', 'lists', 'searchreplace', 'code', 'autoresize'],
  toolbar: 'undo redo removeformat | bold italic underline | bullist numlist | link | code',
}

type ReportFormProps = {
  control: Control<ReportFormValues>
  analysts: Analyst[]
  tags: InternalReportTag[]
  storedReport?: { id: string; fileId: string }
  disabled?: boolean
}

export const ReportForm: FC<ReportFormProps> = ({ control, analysts, tags, storedReport, disabled }) => {
  const { t } = useTranslation()
  const [visibility, published] = useWatch({ control, name: ['visibility', 'published'] })
  const tagNames = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.name])), [tags])
  const tagName = (id: string) => tagNames.get(id) ?? t('internal-analysts:reports_unknown_tag', { id }) ?? id
  const visibilityHelp = reportVisibilityOptions.find((option) => option.value === visibility)?.helpKey

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={3}>
          <Controller
            name="titles"
            control={control}
            rules={{ validate: (value) => !!value.en.trim() }}
            render={({ field, fieldState }) => (
              <Box>
                <InputTranslator
                  value={field.value}
                  onChange={(value) => field.onChange(toTranslations(value))}
                  dialogTitle={t('internal-analysts:reports_title')}
                  label={`${t('internal-analysts:reports_title')} *`}
                  disabled={disabled}
                />
                {fieldState.error && <FormHelperText error>{t('internal-analysts:reports_title_required')}</FormHelperText>}
              </Box>
            )}
          />
          <Controller
            name="summaries"
            control={control}
            render={({ field }) => (
              <InputTranslator
                value={field.value}
                onChange={(value) => field.onChange(toTranslations(value))}
                inputType={InputTranslatorType.WYSIWYG}
                dialogTitle={t('internal-analysts:reports_summary')}
                label={t('internal-analysts:reports_summary')}
                wysiwygConfig={summaryEditorConfig}
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="analysts"
            control={control}
            rules={{ validate: (value) => value.length > 0 }}
            render={({ field, fieldState }) => (
              <ReportAnalystsField value={field.value} onChange={field.onChange} analysts={analysts} error={!!fieldState.error} disabled={disabled} />
            )}
          />
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={Array.from(new Set([...field.value, ...tags.map(({ id }) => id)]))}
                value={field.value}
                onChange={(_event, tagIds) => field.onChange(tagIds)}
                getOptionLabel={tagName}
                filterSelectedOptions
                disabled={disabled}
                renderTags={(tagIds, getTagProps) => tagIds.map((id, index) => <Chip {...getTagProps({ index })} key={id} label={tagName(id)} />)}
                renderInput={(params) => <TextField {...params} size="small" label={t('internal-analysts:reports_tags')} />}
              />
            )}
          />

          <Divider />

          <Controller
            name="fileId"
            control={control}
            rules={{ validate: (value) => !!value }}
            render={({ field, fieldState }) => (
              <ReportFileField value={field.value} onChange={field.onChange} storedReport={storedReport} error={!!fieldState.error} disabled={disabled} />
            )}
          />
          <Controller
            name="coverUrls"
            control={control}
            rules={{ validate: (value) => value.length > 0 }}
            render={({ field, fieldState }) => (
              <ReportCoversField value={field.value} onChange={field.onChange} error={!!fieldState.error} disabled={disabled} />
            )}
          />
          <Controller
            name="gameIds"
            control={control}
            render={({ field }) => <ReportGamesField value={field.value} onChange={field.onChange} disabled={disabled} />}
          />

          <Divider />

          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                size="small"
                label={t('internal-analysts:reports_visibility')}
                helperText={visibilityHelp ? t(visibilityHelp) : undefined}
                sx={{ maxWidth: 320 }}
                disabled={disabled}
              >
                {reportVisibilityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {hasAllowedUsers(visibility) && (
            <Controller
              name="allowedUsers"
              control={control}
              render={({ field }) => <ReportAllowedUsersField value={field.value} onChange={field.onChange} disabled={disabled} />}
            />
          )}

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={disabled} />}
                  label={t('internal-analysts:reports_published')}
                />
              )}
            />
            {published && (
              <Controller
                name="publishedAt"
                control={control}
                rules={{ validate: (value, values) => !values.published || Number.isFinite(value) }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="datetime-local"
                    size="small"
                    required
                    label={t('internal-analysts:reports_published_at')}
                    value={toDateTimeLocalValue(field.value)}
                    onChange={(event) => field.onChange(fromDateTimeLocalValue(event.target.value))}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? t('internal-analysts:reports_published_at_required') : t('internal-analysts:reports_published_at_help')}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 260 }}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
